"use client";

import { ArrowUpRight, BadgeCheck } from "lucide-react";
import { type HTMLMotionProps, motion, type Variants } from "motion/react";

import { EASE_IN_OUT, EASE_OUT_EXPO } from "@/components/motion/reveal";
import { cn } from "@/lib/utils";

import { useReveal } from "../../_lib/use-reveal";

import type { TrainingItem } from "./training-details";

// =============================================================================

const rowVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

/**
 * List item revealing its separator, title and meta once in view (and once
 * the intro preloader has lifted).
 */
export function RevealRow({
  children,
  ...props
}: Omit<HTMLMotionProps<"li">, "ref">) {
  const { ref, isRevealed } = useReveal<HTMLLIElement>();
  return (
    <motion.li
      ref={ref}
      initial="hidden"
      animate={isRevealed ? "visible" : "hidden"}
      variants={rowVariants}
      {...props}
    >
      {children}
    </motion.li>
  );
}

const lineVariants: Variants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 1.4, ease: EASE_OUT_EXPO },
  },
};

const riseVariants: Variants = {
  hidden: { y: "115%" },
  visible: { y: "0%", transition: { duration: 1, ease: EASE_IN_OUT } },
};

const fadeVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8, delay: 0.3 } },
};

// =============================================================================

/** Hairline drawn from the left when the row enters the viewport */
export function RowSeparator({ position }: { position: "top" | "bottom" }) {
  return (
    <motion.span
      aria-hidden
      variants={lineVariants}
      className={cn(
        "absolute inset-x-0 h-px origin-left bg-line",
        position === "top" ? "top-0" : "bottom-0"
      )}
    />
  );
}

/** Thin diagonal slash between the number and the title */
function Slash({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 30 44"
      fill="none"
      aria-hidden
      className={className}
      preserveAspectRatio="none"
    >
      <line
        x1="29"
        y1="0.5"
        x2="1"
        y2="43.5"
        stroke="currentColor"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// =============================================================================

type RowTitleProps = {
  item: TrainingItem;
  isActive?: boolean;
  isDimmed?: boolean;
};

/**
 * "01 / Master of engineering": the number, the slash and the title rise from
 * a mask. The active row nudges its title, the other rows fade. Phrasing
 * content only: the parent provides the heading (and the button).
 */
export function RowTitle({
  item,
  isActive = false,
  isDimmed = false,
}: RowTitleProps) {
  return (
    <span className="mb-[-0.14em] block min-w-0 flex-1 overflow-hidden pb-[0.14em] text-[clamp(1.6rem,3.47vw,3.125rem)] leading-[1.08] tracking-[-0.01em]">
      <motion.span variants={riseVariants} className="block">
        <span
          className={cn(
            "flex items-start transition-opacity duration-500 ease-out-quart",
            isDimmed && "opacity-45"
          )}
        >
          <span className="mt-[0.08em] text-[0.4em] leading-none tabular-nums">
            {item.number}
          </span>
          <Slash className="h-[0.88em] w-[0.62em] shrink-0 text-foreground/70" />
          <span
            data-row-title
            className={cn(
              "ml-[0.24em] min-w-0 font-normal transition-transform duration-700 ease-out-expo",
              isActive && "translate-x-[0.3em]"
            )}
          >
            {item.title}
          </span>
        </span>
      </motion.span>
    </span>
  );
}

// =============================================================================

type RowMetaProps = {
  typeLabel: string;
  verify?: { href: string; label: string; ariaLabel: string; newTab: string };
  isActive?: boolean;
  isDimmed?: boolean;
  className?: string;
};

/**
 * Right side of a row: the kind of entry and, for certifications, the link
 * to the credential.
 */
export function RowMeta({
  typeLabel,
  verify,
  isActive = false,
  isDimmed = false,
  className,
}: RowMetaProps) {
  return (
    <motion.span
      variants={fadeVariants}
      className={cn("flex shrink-0 items-center gap-6", className)}
    >
      <span
        className={cn(
          "text-xs tracking-[0.18em] text-muted-foreground uppercase transition-opacity duration-500",
          (isActive || isDimmed) && "opacity-0"
        )}
      >
        {typeLabel}
      </span>
      {verify && (
        <a
          href={verify.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${verify.ariaLabel} ${verify.newTab}`}
          className={cn(
            "group/verify inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-sm transition-opacity duration-500",
            isDimmed && "opacity-45"
          )}
        >
          <BadgeCheck className="size-4 text-brand" strokeWidth={1.75} />
          {verify.label}
          <ArrowUpRight className="size-3.5 transition-transform duration-500 ease-out-expo group-hover/verify:rotate-45" />
        </a>
      )}
    </motion.span>
  );
}

// =============================================================================
